import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { useAuth } from '../../shared/AuthContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const ACTIVITY_COLORS = {
  very_active:       'success',
  moderately_active: 'info',
  rarely_active:     'warning',
  dormant:           'secondary',
  never_logged_in:   'secondary',
};

function Avatar({ user, size = 40 }) {
  const photo = user.profile_photo;
  const initials = (user.full_name || user.username || '?').charAt(0).toUpperCase();
  if (photo) {
    return <img src={photo} alt="" className="avatar" style={{ width: size, height: size, objectFit: 'cover' }} />;
  }
  return (
    <span className="avatar bg-primary-subtle text-primary fw-bold" style={{ width: size, height: size, fontSize: size * 0.45, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {initials}
    </span>
  );
}

// ── Create user modal ──────────────────────────────────────────────────────

const EMPTY_FORM = { username: '', email: '', first_name: '', last_name: '', password: '', password_confirm: '', is_staff: false };

function CreateUserModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const mutation = useMutation({
    mutationFn: (data) => api.post('accounts/api/users/', data),
    onSuccess: (data) => {
      toast.success(t('profile.management.created'));
      onCreated(data);
    },
    onError: (err) => {
      if (err?.response?.data && typeof err.response.data === 'object') {
        setErrors(err.response.data);
      } else {
        toast.error(t('profile.management.createError'));
      }
    },
  });

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{t('profile.management.newUser')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">{t('profile.management.fields.firstName')}</label>
                  <input className="form-control" value={form.first_name} onChange={set('first_name')} />
                  {errors.first_name && <div className="invalid-feedback d-block">{errors.first_name}</div>}
                </div>
                <div className="col-6">
                  <label className="form-label">{t('profile.management.fields.lastName')}</label>
                  <input className="form-control" value={form.last_name} onChange={set('last_name')} />
                  {errors.last_name && <div className="invalid-feedback d-block">{errors.last_name}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label required">{t('profile.management.fields.username')}</label>
                  <input className={`form-control ${errors.username ? 'is-invalid' : ''}`} value={form.username} onChange={set('username')} required />
                  {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">{t('profile.management.fields.email')}</label>
                  <input className={`form-control ${errors.email ? 'is-invalid' : ''}`} type="email" value={form.email} onChange={set('email')} />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
                <div className="col-6">
                  <label className="form-label required">{t('profile.management.fields.password')}</label>
                  <input className={`form-control ${errors.password ? 'is-invalid' : ''}`} type="password" value={form.password} onChange={set('password')} required />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>
                <div className="col-6">
                  <label className="form-label required">{t('profile.management.fields.passwordConfirm')}</label>
                  <input className={`form-control ${errors.password_confirm ? 'is-invalid' : ''}`} type="password" value={form.password_confirm} onChange={set('password_confirm')} required />
                  {errors.password_confirm && <div className="invalid-feedback">{errors.password_confirm}</div>}
                </div>
                <div className="col-12">
                  <label className="form-check">
                    <input type="checkbox" className="form-check-input" checked={form.is_staff} onChange={set('is_staff')} />
                    <span className="form-check-label">{t('profile.management.fields.isStaff')}</span>
                  </label>
                </div>
              </div>
              {errors.non_field_errors && <div className="alert alert-danger mt-2">{errors.non_field_errors}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link text-secondary" onClick={onClose}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary ms-auto" disabled={mutation.isPending}>
                {mutation.isPending
                  ? <span className="spinner-border spinner-border-sm me-1" />
                  : <i className="ti ti-user-plus me-1" />}
                {t('profile.management.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Main screen ────────────────────────────────────────────────────────────

export default function UserManagement() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const params = new URLSearchParams();
  if (search) params.set('q', search);
  if (filterActive !== '') params.set('is_active', filterActive);

  const { data, isLoading } = useQuery({
    queryKey: ['users-list', search, filterActive],
    queryFn: () => api.get(`accounts/api/users/?${params}`),
  });

  useRealtimeEvents((e) => {
    if (e.model === 'auth.user') qc.invalidateQueries({ queryKey: ['users-list'] });
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }) => api.post(`accounts/api/users/${id}/${active ? 'activate' : 'deactivate'}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users-list'] }),
    onError: () => toast.error(t('common.error')),
  });

  const users = Array.isArray(data) ? data : (data?.results ?? []);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-fluid px-4">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">{t('profile.management.pretitle')}</div>
              <h2 className="page-title">{t('profile.management.title')}</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <i className="ti ti-user-plus me-1" />
                {t('profile.management.newUser')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-fluid px-4">
          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-center">
                <div className="col">
                  <div className="input-group">
                    <span className="input-group-text"><i className="ti ti-search" /></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t('profile.management.searchPlaceholder')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-auto">
                  <select className="form-select" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
                    <option value="">{t('profile.management.filterAll')}</option>
                    <option value="true">{t('profile.management.filterActive')}</option>
                    <option value="false">{t('profile.management.filterInactive')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card">
            <div className="table-responsive">
              <table className="table table-vcenter card-table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: 64 }} />
                    <th>{t('profile.management.cols.name')}</th>
                    <th>{t('profile.management.cols.email')}</th>
                    <th>{t('profile.management.cols.role')}</th>
                    <th>{t('profile.management.cols.activity')}</th>
                    <th>{t('profile.management.cols.status')}</th>
                    <th className="w-1" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan={7}><CenteredLoader /></td></tr>
                  ) : users.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-secondary py-4">{t('profile.management.empty')}</td></tr>
                  ) : users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <Avatar user={u} />
                      </td>
                      <td>
                        <Link to={`/accounts/profile/${u.id}`} className="fw-medium text-reset">
                          {u.full_name || u.username}
                        </Link>
                        <div className="text-secondary small">{u.username}</div>
                      </td>
                      <td className="text-secondary">{u.email || '—'}</td>
                      <td>
                        {u.is_staff
                          ? <span className="badge bg-purple-lt">{t('profile.management.admin')}</span>
                          : <span className="badge bg-secondary-lt">{t('profile.management.user')}</span>}
                      </td>
                      <td>
                        <span className={`badge bg-${ACTIVITY_COLORS[u.activity_status] || 'secondary'}-lt`}>
                          {t(`profile.management.activity.${u.activity_status}`, u.activity_status)}
                        </span>
                      </td>
                      <td>
                        {u.is_active
                          ? <span className="badge bg-success-lt">{t('profile.management.active')}</span>
                          : <span className="badge bg-danger-lt">{t('profile.management.inactive')}</span>}
                      </td>
                      <td>
                        <div className="btn-group">
                          <Link to={`/accounts/profile/${u.id}`} className="btn btn-sm btn-ghost-secondary" title={t('profile.management.viewProfile')}>
                            <i className="ti ti-eye" />
                          </Link>
                          {u.id !== currentUser?.id && (
                            <button
                              className={`btn btn-sm btn-ghost-${u.is_active ? 'danger' : 'success'}`}
                              title={u.is_active ? t('profile.management.deactivate') : t('profile.management.activate')}
                              onClick={() => toggleActive.mutate({ id: u.id, active: !u.is_active })}
                            >
                              <i className={`ti ti-${u.is_active ? 'user-off' : 'user-check'}`} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['users-list'] });
          }}
        />
      )}
    </div>
  );
}
